import { useEffect } from "react";
import { apiClient, useApiQuery, useApiRevalidate } from "../../services/api";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";

/**
 * The posters that finished generating and the reader has not acknowledged.
 *
 * The state is the `VisualAbstractReady` personal notification the generation
 * job writes, unread — NOT a per-device "last visit" timestamp. A poster is
 * generated on one device and read on another, and the badge has to survive
 * that; the notification already crosses devices, already expires with the rest
 * of the notification centre, and is the same row the centre lists. A second
 * freshness store beside it would be a second answer to one question.
 *
 * It reads the notification feed the profile header and the notification centre
 * already read, on the same cache key, so the several surfaces that show the dot
 * share one request rather than each asking their own question.
 */
function useUnseenVisualAbstractIds(): string[] {
  const { currentUser } = useCurrentUser();
  const personals = useApiQuery(
    "/webapp/private/notification/personals",
    currentUser === null ? false : {}
  );
  if (personals.state !== "success") return [];
  return personals.body
    .filter(
      (notification) =>
        notification.type === "VisualAbstractReady" &&
        notification.read === false
    )
    .map((notification) => notification.id);
}

/** Whether to show the red dot beside JuisciGPT and its Visual abstracts row. */
export function useHasUnseenVisualAbstracts(): boolean {
  return useUnseenVisualAbstractIds().length > 0;
}

/**
 * Acknowledges every finished poster, because the reader is looking at them.
 *
 * Mounted by the library, which is where the badge points: arriving there IS
 * the acknowledgement, exactly as opening the notification centre is. Marking
 * them read on the server rather than locally is what puts the badge out on the
 * reader's other devices too.
 */
export function useAcknowledgeVisualAbstracts(): void {
  const revalidate = useApiRevalidate();
  // The list is a fresh array on every render, so the effect keys on its
  // CONTENT. That is also what makes it self-terminating without a latch: the
  // revalidation below empties the list, which is the only way the key moves
  // again — and a poster that lands while the library is open re-arms it,
  // which is the behaviour we want.
  const idsKey = useUnseenVisualAbstractIds().join(",");

  useEffect(() => {
    if (idsKey === "") return;
    void apiClient
      .post("/webapp/private/notification/personals/mark-as-read", {
        json: { ids: idsKey.split(",") },
      })
      .then(() => revalidate("/webapp/private/notification/personals"));
  }, [idsKey, revalidate]);
}
